<template>
    <div class="grid">
        <Toast/>

        <div class="col-12">
            <div class="card">
                <h4>Activos fijos</h4>
                <p style="max-width: 760px;">
                    Lo que compras para <b>usar</b>, no para vender: muebles, equipos, vehículos,
                    inmuebles. No son inventario ni gasto del mes — se registran como un bien tuyo y
                    su costo se reparte a lo largo de su vida útil. Esa depreciación es
                    <b>gasto deducible</b>, así que registrarlos te conviene.
                </p>

                <div style="display:flex; gap:0.5rem; flex-wrap:wrap;">
                    <Button label="Nuevo activo" icon="pi pi-plus" @click="openNew"/>
                    <Button label="Depreciar un mes" icon="pi pi-calendar" class="p-button-outlined"
                            @click="depreciateDialog = true"/>
                </div>

                <DataTable :value="assets" :loading="loading" class="mt-3" :scrollable="true">
                    <Column header="Activo" style="min-width: 200px;">
                        <template #body="slotProps">
                            <div>
                                {{ slotProps.data.name }}
                                <small style="display:block; color:#777;">{{ slotProps.data.assetTypeLabel }}</small>
                            </div>
                        </template>
                    </Column>
                    <Column header="Adquirido" style="width: 120px;">
                        <template #body="slotProps">
                            <small>{{ slotProps.data.acquisitionDate }}</small>
                        </template>
                    </Column>
                    <Column header="Costo" style="width: 120px;">
                        <template #body="slotProps">${{ money(slotProps.data.cost) }}</template>
                    </Column>
                    <Column header="Depreciado" style="width: 130px;">
                        <template #body="slotProps">
                            <div>
                                ${{ money(slotProps.data.accumulated) }}
                                <small style="display:block; color:#777;">${{ money(slotProps.data.monthly) }}/mes</small>
                            </div>
                        </template>
                    </Column>
                    <Column header="Valor actual" style="width: 130px;">
                        <template #body="slotProps">
                            <b>${{ money(slotProps.data.bookValue) }}</b>
                        </template>
                    </Column>
                    <Column header="Estado" style="width: 150px;">
                        <template #body="slotProps">
                            <Tag :severity="statusSeverity(slotProps.data.status)"
                                 :value="statusLabel(slotProps.data.status)"/>
                        </template>
                    </Column>
                    <Column header="" style="width: 130px;">
                        <template #body="slotProps">
                            <Button v-if="slotProps.data.status !== 'DISPOSED'"
                                    label="Dar de baja" class="p-button-sm p-button-outlined"
                                    @click="openDisposal(slotProps.data)"/>
                        </template>
                    </Column>
                </DataTable>

                <Message v-if="!loading && !assets.length" severity="info" :closable="false" class="mt-3">
                    Todavía no tienes activos registrados.
                </Message>
            </div>
        </div>

        <!-- ===== Alta ===== -->
        <Dialog v-model:visible="newDialog" header="Nuevo activo fijo" :modal="true" :style="{width: '520px'}">
            <div style="display:flex; flex-direction:column; gap:0.9rem;">
                <div>
                    <label style="display:block; font-size:0.85rem; color:#777;">¿Qué es?</label>
                    <InputText v-model="form.name" placeholder="Escritorio de gerencia" style="width:100%;"/>
                </div>
                <div>
                    <label style="display:block; font-size:0.85rem; color:#777;">Tipo</label>
                    <Dropdown v-model="form.assetType" :options="types" optionLabel="label"
                              optionValue="value" style="width:100%;" @change="onTypeChange"/>
                    <small style="color:#777;">Define en cuántos años se reparte el costo.</small>
                </div>
                <div style="display:flex; gap:0.75rem;">
                    <div style="flex:1;">
                        <label style="display:block; font-size:0.85rem; color:#777;">Fecha de compra</label>
                        <Calendar v-model="form.acquisitionDate" dateFormat="yy-mm-dd" style="width:100%;"/>
                    </div>
                    <div style="flex:1;">
                        <label style="display:block; font-size:0.85rem; color:#777;">Vida útil (años)</label>
                        <InputNumber v-model="form.usefulLifeYears" style="width:100%;"/>
                    </div>
                </div>
                <div style="display:flex; gap:0.75rem;">
                    <div style="flex:1;">
                        <label style="display:block; font-size:0.85rem; color:#777;">Costo (sin IVA)</label>
                        <InputNumber v-model="form.cost" mode="decimal" :minFractionDigits="2"
                                     :maxFractionDigits="2" style="width:100%;"/>
                    </div>
                    <div style="flex:1;">
                        <label style="display:block; font-size:0.85rem; color:#777;">IVA</label>
                        <InputNumber v-model="form.tax" mode="decimal" :minFractionDigits="2"
                                     :maxFractionDigits="2" style="width:100%;"/>
                    </div>
                </div>

                <div class="field-checkbox">
                    <Checkbox v-model="form.isOpening" :binary="true" inputId="isOpening"/>
                    <label for="isOpening" style="margin-left:0.5rem;">Ya lo tenía antes de usar Contabilidad</label>
                </div>

                <template v-if="form.isOpening">
                    <Message severity="info" :closable="false">
                        No se registrará una compra: ese valor ya entró en tus saldos de apertura.
                        Indica cuánto llevaba depreciado para que la depreciación continúe desde ahí.
                    </Message>
                    <div>
                        <label style="display:block; font-size:0.85rem; color:#777;">Ya depreciado</label>
                        <InputNumber v-model="form.openingAccumulated" mode="decimal"
                                     :minFractionDigits="2" :maxFractionDigits="2" style="width:100%;"/>
                    </div>
                </template>
                <template v-else>
                    <div>
                        <label style="display:block; font-size:0.85rem; color:#777;">Proveedor</label>
                        <InputText v-model="form.vendorName" placeholder="A quién se lo compraste" style="width:100%;"/>
                    </div>
                    <!-- Se paga como cualquier compra (H20): al contado sale de una cuenta, a crédito queda en Por Pagar. -->
                    <div>
                        <label style="display:block; font-size:0.85rem; color:#777;">Forma de pago</label>
                        <SelectButton v-model="form.paymentTerm" :options="paymentTerms" optionLabel="label" optionValue="value"/>
                    </div>
                    <div v-if="form.paymentTerm === 'contado'">
                        <label style="display:block; font-size:0.85rem; color:#777;">Cuenta</label>
                        <Dropdown v-model="form.accountId" :options="accounts" optionLabel="name" optionValue="id"
                                  placeholder="De dónde salió el dinero" style="width:100%;"/>
                    </div>
                    <div v-if="form.paymentTerm === 'credito'">
                        <label style="display:block; font-size:0.85rem; color:#777;">Plazo (días)</label>
                        <InputNumber v-model="form.creditDays" :min="1" :useGrouping="false" style="width:100%;"/>
                    </div>
                </template>
            </div>
            <template #footer>
                <Button label="Cancelar" class="p-button-text" @click="newDialog = false"/>
                <Button label="Registrar" icon="pi pi-check" :loading="working" @click="createAsset"/>
            </template>
        </Dialog>

        <!-- ===== Baja ===== -->
        <Dialog v-model:visible="disposalDialog" :header="'Dar de baja: ' + (current.name || '')"
                :modal="true" :style="{width: '480px'}">
            <div style="display:flex; flex-direction:column; gap:0.9rem;">
                <p style="margin:0; color:#555;">
                    Valor actual en libros: <b>${{ money(current.bookValue) }}</b>
                </p>
                <div>
                    <label style="display:block; font-size:0.85rem; color:#777;">Fecha</label>
                    <Calendar v-model="disposal.disposalDate" dateFormat="yy-mm-dd" style="width:100%;"/>
                </div>
                <div class="field-checkbox">
                    <Checkbox v-model="disposal.isSale" :binary="true" inputId="isSale"/>
                    <label for="isSale" style="margin-left:0.5rem;">Lo estoy vendiendo</label>
                </div>
                <!-- La venta es a un cliente: salía a CONSUMIDOR FINAL (H21). -->
                <div v-if="disposal.isSale">
                    <label style="display:block; font-size:0.85rem; color:#777;">Cliente</label>
                    <AutoComplete v-model="disposal.customer" :suggestions="customerOptions" field="name"
                                  :dropdown="false" :multiple="false" placeholder="Busca por nombre o identificación"
                                  @complete="searchCustomers($event)" style="width:100%;" inputStyle="width:100%;"/>
                </div>
                <div v-if="disposal.isSale" style="display:flex; gap:0.75rem;">
                    <div style="flex:1;">
                        <label style="display:block; font-size:0.85rem; color:#777;">Precio (sin IVA)</label>
                        <InputNumber v-model="disposal.salePrice" mode="decimal" :minFractionDigits="2"
                                     :maxFractionDigits="2" style="width:100%;"/>
                    </div>
                    <div style="flex:1;">
                        <label style="display:block; font-size:0.85rem; color:#777;">IVA</label>
                        <InputNumber v-model="disposal.tax" mode="decimal" :minFractionDigits="2"
                                     :maxFractionDigits="2" style="width:100%;"/>
                    </div>
                </div>
                <Message severity="info" :closable="false">
                    <span v-if="disposal.isSale">
                        Se registra la venta para que puedas facturarla. No cuenta como ingreso por
                        ventas: la diferencia contra el valor en libros es ganancia o pérdida.
                    </span>
                    <span v-else>
                        Sin venta, todo el valor en libros se registra como pérdida.
                    </span>
                </Message>
            </div>
            <template #footer>
                <Button label="Cancelar" class="p-button-text" @click="disposalDialog = false"/>
                <Button label="Dar de baja" icon="pi pi-check" :loading="working" @click="disposeAsset"/>
            </template>
        </Dialog>

        <!-- ===== Depreciar un mes ===== -->
        <Dialog v-model:visible="depreciateDialog" header="Depreciar un mes" :modal="true" :style="{width: '420px'}">
            <p style="color:#555;">
                La depreciación corre sola cada mes. Úsalo solo para ponerte al día si registraste
                un activo con fecha pasada — repetirlo no duplica nada.
            </p>
            <div style="display:flex; gap:0.75rem;">
                <div style="flex:1;">
                    <label style="display:block; font-size:0.85rem; color:#777;">Mes</label>
                    <InputNumber v-model="period.month" :min="1" :max="12" style="width:100%;"/>
                </div>
                <div style="flex:1;">
                    <label style="display:block; font-size:0.85rem; color:#777;">Año</label>
                    <InputNumber v-model="period.year" :useGrouping="false" style="width:100%;"/>
                </div>
            </div>
            <template #footer>
                <Button label="Cancelar" class="p-button-text" @click="depreciateDialog = false"/>
                <Button label="Calcular" icon="pi pi-check" :loading="working" @click="depreciate"/>
            </template>
        </Dialog>
    </div>
</template>

<script>
import httpRequest from '../../../service/httpRequest';
import { PAYMENT_TERMS, assetPaymentFields, disposalCustomer } from '../../../service/assetPayment';

export default {
    data() {
        const now = new Date();
        return {
            loading: true,
            working: false,
            assets: [],
            types: [],
            newDialog: false,
            disposalDialog: false,
            depreciateDialog: false,
            current: {},
            form: {},
            disposal: {},
            period: {year: now.getFullYear(), month: now.getMonth() + 1},
            paymentTerms: PAYMENT_TERMS,
            customerOptions: [],
        };
    },
    computed: {
        // Cuentas de tesorería de la actividad, para pagar el activo al contado (H20).
        accounts() {
            return this.$store.getters.finantialAccounts || [];
        },
    },
    async mounted() {
        await this.load();
    },
    methods: {
        money(value) {
            return parseFloat(value || 0).toFixed(2);
        },
        statusLabel(status) {
            return {ACTIVE: 'En uso', FULLY_DEPRECIATED: 'Totalmente depreciado',
                    DISPOSED: 'Dado de baja'}[status] || status;
        },
        statusSeverity(status) {
            return {ACTIVE: 'success', FULLY_DEPRECIATED: 'warning',
                    DISPOSED: 'danger'}[status] || 'info';
        },
        asDate(value) {
            if (!value) return null;
            if (typeof value === 'string') return value;
            // El Calendar devuelve Date; el backend espera ISO (yyyy-mm-dd).
            const pad = n => String(n).padStart(2, '0');
            return value.getFullYear() + '-' + pad(value.getMonth() + 1) + '-' + pad(value.getDate());
        },
        async load() {
            this.loading = true;
            const response = await httpRequest.getAccountingFixedAssets();
            this.loading = false;
            if (response.status === 200) {
                this.assets = response.data.assets;
                this.types = response.data.types;
            } else {
                this.$toast.add({severity: 'error', summary: 'Error',
                                 detail: 'No se pudieron cargar los activos', life: 4000});
            }
        },
        openNew() {
            this.form = {name: '', assetType: 'EQUIPMENT', acquisitionDate: new Date(),
                         usefulLifeYears: 10, cost: null, tax: null, isOpening: false,
                         openingAccumulated: null, vendorName: '',
                         paymentTerm: null, accountId: null, creditDays: 30};
            this.newDialog = true;
        },
        onTypeChange(event) {
            // La vida util por defecto sale del tipo (tasas maximas del SRI).
            const type = this.types.find(t => t.value === event.value);
            if (type) this.form.usefulLifeYears = type.usefulLifeYears;
        },
        async createAsset() {
            const payment = assetPaymentFields(this.form);
            if (payment.error) {
                this.$toast.add({severity: 'warn', summary: 'Falta un dato', detail: payment.error, life: 4000});
                return;
            }
            this.working = true;
            const { paymentTerm, accountId, creditDays, ...base } = this.form;   // eslint-disable-line no-unused-vars
            const payload = {...base, ...payment.fields, acquisitionDate: this.asDate(this.form.acquisitionDate)};
            const response = await httpRequest.createAccountingFixedAsset(payload);
            this.working = false;
            if (response.status === 200) {
                this.newDialog = false;
                this.$toast.add({severity: 'success', summary: 'Activo registrado', life: 4000});
                await this.load();
            } else {
                this.$toast.add({severity: 'error', summary: 'No se pudo registrar',
                                 detail: response.error || '', life: 5000});
            }
        },
        openDisposal(asset) {
            this.current = asset;
            this.disposal = {disposalDate: new Date(), isSale: false, salePrice: null, tax: null, customer: null};
            this.disposalDialog = true;
        },
        async searchCustomers(event) {
            const query = (event.query || '').trim();
            if (query.length < 2) { this.customerOptions = []; return; }
            const response = await httpRequest.getCustomers(query);
            this.customerOptions = response.status === 200 ? response.data : [];
        },
        async disposeAsset() {
            const customer = disposalCustomer(this.disposal);
            if (customer.error) {
                this.$toast.add({severity: 'warn', summary: 'Falta un dato', detail: customer.error, life: 4000});
                return;
            }
            this.working = true;
            const payload = {
                assetId: this.current.id,
                customerId: customer.customerId,
                disposalDate: this.asDate(this.disposal.disposalDate),
                salePrice: this.disposal.isSale ? String(this.disposal.salePrice || 0) : '0',
                tax: this.disposal.isSale ? String(this.disposal.tax || 0) : '0',
            };
            const response = await httpRequest.disposeAccountingFixedAsset(payload);
            this.working = false;
            if (response.status === 200) {
                this.disposalDialog = false;
                this.$toast.add({severity: 'success', summary: 'Activo dado de baja', life: 4000});
                await this.load();
            } else {
                this.$toast.add({severity: 'error', summary: 'No se pudo dar de baja',
                                 detail: response.error || '', life: 5000});
            }
        },
        async depreciate() {
            this.working = true;
            const response = await httpRequest.runAccountingDepreciation(
                this.period.year, this.period.month);
            this.working = false;
            if (response.status === 200) {
                this.depreciateDialog = false;
                this.$toast.add({severity: 'success', summary: 'Depreciación calculada',
                                 detail: response.data.posted + ' activo(s)', life: 4000});
                await this.load();
            } else {
                this.$toast.add({severity: 'error', summary: 'Error',
                                 detail: response.error || '', life: 5000});
            }
        },
    },
}
</script>
